import { useEffect } from 'react';
import { useKeyboardOpen } from './useKeyboardOpen';
import { usePlatform } from './usePlatformUtils';

export function useChatPageScrollLock(pageRef, scrollContainerRef) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add('chat-page-active');
    body.classList.add('chat-page-active');

    const preventPageScroll = (event) => {
      let node = event.target;
      while (node && node !== pageRef.current) {
        if (node === scrollContainerRef.current) return;
        node = node.parentNode;
      }
      event.preventDefault();
    };

    const page = pageRef.current;
    if (page) {
      page.addEventListener('touchmove', preventPageScroll, { passive: false });
    }

    return () => {
      html.classList.remove('chat-page-active');
      body.classList.remove('chat-page-active');
      if (page) {
        page.removeEventListener('touchmove', preventPageScroll);
      }
    };
  }, [pageRef, scrollContainerRef]);
}

export function useChatMobileViewport({
  pageRef,
  scrollContainerRef,
  scrollToBottom,
  disableViewportAdjust = false,
}) {
  const platform = usePlatform();
  const { isKeyboardOpen, keyboardHeight } = useKeyboardOpen();

  useEffect(() => {
    if (platform !== 'ios-pwa' || disableViewportAdjust) return undefined;

    const header = document.getElementById('cm_chat_header');
    const wrapper = scrollContainerRef.current;
    if (!header || !wrapper) return undefined;

    header.style.top = `${keyboardHeight}px`;
    wrapper.style.height = `calc(100% - ${keyboardHeight}px)`;
    wrapper.style.marginTop = `${keyboardHeight}px`;

    return () => {
      header.style.top = '';
      wrapper.style.height = '';
      wrapper.style.marginTop = '';
    };
  }, [platform, keyboardHeight, scrollContainerRef, disableViewportAdjust]);

  useEffect(() => {
    if (platform !== 'android' || disableViewportAdjust) return undefined;
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;

    let rafId = null;
    const pageElement = pageRef.current;

    const sync = () => {
      rafId = null;
      const visualViewport = window.visualViewport;
      if (!visualViewport || !pageElement) return;

      const offsetTop = visualViewport.offsetTop || 0;
      pageElement.style.top = `${offsetTop}px`;
      pageElement.style.height = `${visualViewport.height}px`;
      pageElement.style.bottom = 'auto';

      document.documentElement.style.setProperty(
        '--chat-header-offset',
        `${offsetTop}px`
      );
    };

    const scheduleSync = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(sync);
    };

    sync();
    window.visualViewport.addEventListener('resize', scheduleSync);
    window.visualViewport.addEventListener('scroll', scheduleSync);

    return () => {
      window.visualViewport.removeEventListener('resize', scheduleSync);
      window.visualViewport.removeEventListener('scroll', scheduleSync);
      if (rafId) cancelAnimationFrame(rafId);

      if (pageElement) {
        pageElement.style.top = '';
        pageElement.style.height = '';
        pageElement.style.bottom = '';
      }
      document.documentElement.style.setProperty('--chat-header-offset', '0px');
    };
  }, [platform, pageRef, disableViewportAdjust]);

  useEffect(() => {
    if (disableViewportAdjust) return;
    if (isKeyboardOpen && keyboardHeight > 0) {
      requestAnimationFrame(() => scrollToBottom?.('smooth'));
    }
  }, [isKeyboardOpen, keyboardHeight, scrollToBottom, disableViewportAdjust]);

  useEffect(() => {
    if (disableViewportAdjust) return;
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;

    let prevKeyboardGap = 0;
    const applyViewport = () => {
      const keyboardGap = Math.max(0, window.innerHeight - window.visualViewport.height);
      if (keyboardGap > prevKeyboardGap && prevKeyboardGap < 50) {
        requestAnimationFrame(() => scrollToBottom?.('smooth'));
      }
      prevKeyboardGap = keyboardGap;
    };

    window.visualViewport.addEventListener('resize', applyViewport);
    return () => window.visualViewport.removeEventListener('resize', applyViewport);
  }, [scrollToBottom, disableViewportAdjust]);
}
